/**
 * RichHtmlField — re-render-proof contentEditable HTML editor (DOM island).
 *
 * Three guarantees:
 *
 * 1. Commit on input, not blur: `onInput` fires `onChange(innerHTML)` on every
 *    keystroke, so draft state always mirrors the DOM. Even if a re-render
 *    occurs mid-edit, the parent already has the latest text.
 *
 * 2. Uncontrolled DOM ownership: React renders `<div contentEditable />` with
 *    NO children and no `dangerouslySetInnerHTML`. React reconciles zero
 *    children and never touches the element's `innerHTML`, by construction
 *    rather than by identity trick. Caret position and DOM state are preserved
 *    on every parent re-render where `seedKey` is unchanged.
 *
 * 3. Explicit re-seed key (seedKey): callers change `seedKey` when an
 *    external write should replace the DOM content (AI compose, undo,
 *    external reset, or entity/action switch). A `useLayoutEffect` owns the
 *    `innerHTML` assignment and fires synchronously before paint — avoiding
 *    any first-paint flash of stale/empty content. `seedKey` accepts any
 *    string, number, or readonly array of strings/numbers — use the
 *    entity/action identity directly (e.g. `actionId`), a bumped numeric
 *    counter for AI writes, or a composite array `[entityId, version]` for
 *    callers that need both dimensions. Array keys are compared element-wise,
 *    so a new array object with the same elements does NOT trigger a re-seed.
 *
 * 4. Sanitized paste: the native paste would drop raw clipboard HTML straight
 *    into the DOM, bypassing the seed-time DOMPurify gate. `onPaste` takes it
 *    over — sanitize, insert at the caret imperatively (still no React
 *    children), commit through the same `onChange` path as `onInput`.
 *
 * This mirrors the `actionId` re-seed pattern from PR #710 (ActionFormModal).
 */

import { useLayoutEffect, useRef } from 'react';
import type { Key } from 'react';
import DOMPurify from 'dompurify';

/**
 * Insert `content` at the collapsed/expanded caret inside `root`, replacing any
 * selected content. Falls back to appending at the end of `root` when there is
 * no live selection inside the editor (e.g. a programmatic paste, or a paste
 * that arrives while focus lives elsewhere).
 *
 * DOM-island discipline: this mutates the editable element directly. React
 * never owns these children, so there is no reconciliation to fight.
 */
function insertAtCaret(root: HTMLElement, content: Node) {
  const lastInserted =
    content.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? content.lastChild : content;

  const selection = window.getSelection();
  const range =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  if (!range || !root.contains(range.commonAncestorContainer)) {
    root.appendChild(content);
    return;
  }

  range.deleteContents();
  range.insertNode(content);

  // Collapse the caret to just after the pasted content so a subsequent paste
  // or keystroke continues from there rather than re-selecting it.
  if (lastInserted) {
    range.setStartAfter(lastInserted);
    range.setEndAfter(lastInserted);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
}

/** Compare two seedKey values for equality. Arrays are compared element-wise. */
function seedKeysEqual(
  a: Key | readonly Key[] | null,
  b: Key | readonly Key[],
): boolean {
  if (a === null) return false;
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray !== bIsArray) return false;
  if (aIsArray && bIsArray) {
    const aa = a as readonly Key[];
    const bb = b as readonly Key[];
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i++) {
      if (!Object.is(aa[i], bb[i])) return false;
    }
    return true;
  }
  return Object.is(a, b);
}

export type RichHtmlFieldProps = {
  /** Current HTML value — used only on mount and when seedKey changes. */
  value: string;
  /**
   * Change to re-seed the DOM from `value`. Keep stable (same value)
   * to let the user edit without interference from parent re-renders.
   * Accepts a string, number, or readonly array of strings/numbers.
   * Pass the entity/action identity directly (e.g. `actionId`), a bumped
   * numeric counter for AI writes, or a composite array `[entityId, version]`
   * for callers that need both dimensions. Array keys are compared
   * element-wise: a new array object with identical elements does NOT re-seed.
   */
  seedKey: Key | readonly Key[];
  /** Called with the new innerHTML on every input event (not just blur). */
  onChange: (html: string) => void;
  className?: string;
  disabled?: boolean;
  /**
   * Optional accessible name for the contentEditable element. Additive-only
   * passthrough (U1, Gong Flow follow-up bundle bug 1): no `<label>` can bind
   * to a contentEditable div (labels only bind to labelable elements), so
   * callers that previously relied on a wrapping `<label>` for a name must
   * supply one explicitly here instead. Omitted -> no `aria-label` attribute,
   * so the other five `RichHtmlField` consumers are unaffected.
   */
  'aria-label'?: string;
};

export function RichHtmlField({
  value,
  seedKey,
  onChange,
  className,
  disabled = false,
  'aria-label': ariaLabel,
}: RichHtmlFieldProps) {
  const divRef = useRef<HTMLDivElement>(null);
  // Track the last seedKey we actually applied so we only re-seed when it
  // changes. Stored as Key | readonly Key[] | null (null = never applied).
  // Element-wise comparison is done via seedKeysEqual.
  //
  // DOMPurify runs HERE — at seed application — and nowhere else. With
  // commit-on-input, `value` changes identity on every keystroke; sanitizing
  // on every render would mean a full-document DOMPurify pass per keypress
  // whose result is discarded (it is only consumed when the seed changes).
  const appliedSeedRef = useRef<Key | readonly Key[] | null>(null);

  // useLayoutEffect fires synchronously after DOM mutations, before the
  // browser paints — this is critical to avoid a first-paint flash of stale
  // or empty content on seedKey changes (e.g. AI compose writes).
  //
  // We cannot use [seedKey] as the deps array when seedKey may be an array,
  // because a new array object with the same elements is still a different
  // reference and would run the effect every render. Instead we store the
  // applied key in a ref and compare element-wise ourselves.
  //
  // No children are passed to the div below, so React never touches innerHTML
  // after mount — by construction, not by identity trick. This means the
  // effect is the single authority over innerHTML, and it only fires when the
  // seed genuinely changes.
  useLayoutEffect(() => {
    if (!divRef.current) return;
    if (seedKeysEqual(appliedSeedRef.current, seedKey)) return;
    divRef.current.innerHTML = DOMPurify.sanitize(value);
    appliedSeedRef.current = seedKey;
  });

  return (
    <div
      ref={divRef}
      contentEditable={!disabled}
      aria-label={ariaLabel}
      onInput={(e) => {
        onChange((e.currentTarget as HTMLDivElement).innerHTML);
      }}
      // Link click handling (Gong Flow follow-up bundle, live-QA finding:
      // "I still can't open links"). A plain click inside an editable
      // contentEditable region already just places the caret in every major
      // browser — no navigation occurs by default — so there was never a way
      // to actually visit a link's target from here. Cmd/Ctrl+click opens it
      // in a new tab (the Gmail/Docs/Notion convention) without disturbing
      // the plain-click-to-edit path. When `disabled` (read-only render of
      // this same field), there is no caret to protect, so a plain click
      // opens the link directly instead of silently doing nothing.
      onClick={(e) => {
        const anchor = (e.target as HTMLElement).closest('a');
        if (!anchor) return;
        if (disabled || e.metaKey || e.ctrlKey) {
          e.preventDefault();
          window.open(anchor.href, '_blank', 'noopener,noreferrer');
          return;
        }
        e.preventDefault();
      }}
      // Paste is the ONLY other way arbitrary HTML enters this DOM island, and
      // the browser's native paste is unsanitized. We take it over: sanitize
      // with the same DOMPurify configuration the seed path uses (:108), insert
      // at the caret ourselves, then commit through the same onChange path as
      // onInput so the parent sees the new value.
      //
      // Never install a global DOMPurify hook here — the module is shared with
      // descriptionContent.tsx and a hook would change sanitization app-wide.
      onPaste={(e) => {
        if (disabled) return;
        e.preventDefault();

        const el = e.currentTarget as HTMLDivElement;
        const clipboard = e.clipboardData;
        const html = clipboard?.getData('text/html') ?? '';
        const text = clipboard?.getData('text/plain') ?? '';

        let content: Node | null = null;
        if (html) {
          // <template> parses markup inertly (no scripts run, no resources
          // fetched), and what it parses is the sanitizer's output.
          const template = document.createElement('template');
          template.innerHTML = DOMPurify.sanitize(html);
          content = template.content.firstChild ? template.content : null;
        } else if (text) {
          // No HTML flavor: insert as text, never as markup.
          content = document.createTextNode(text);
        }

        // Empty clipboard (or markup that sanitized down to nothing): no-op,
        // and notably no spurious onChange.
        if (!content) return;

        insertAtCaret(el, content);
        onChange(el.innerHTML);
      }}
      className={className}
      suppressContentEditableWarning
    />
  );
}
